import axios from "axios";
import { toast } from "@/lib/toast";
import { clearImpersonation, getPortalToken, isImpersonating } from "@/lib/portalSession";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Pas de withCredentials : en prod le frontend est cross-origin de l'API et le proxy
// renvoie Access-Control-Allow-Origin '*'. Auth par Bearer JWT (les cookies httpOnly
// restent fonctionnels en same-origin).
export const api = axios.create({ baseURL: API });

/** Helpers for components needing direct fetch (e.g. multipart upload). */
export const getApiBase = () => API;
export const authToken = () => localStorage.getItem("token") || "";

// Batch #14.255 — en-têtes d'auth centralisés (remplacent ~45 copies locales de `authH`)
export const authH = () => {
  const t = localStorage.getItem("adminToken") || localStorage.getItem("token");
  return t ? { Authorization: `Bearer ${t}` } : {};
};
export const portalAuthH = () => {
  const t = getPortalToken() || localStorage.getItem("token");
  return t ? { Authorization: `Bearer ${t}` } : {};
};

api.interceptors.request.use((config) => {
  // Path-aware token selection to prevent cross-scope contamination.
  // A /portal/* call MUST use portalToken only, otherwise must use admin token.
  // EXCEPTION: /portal/admin/* are admin-side endpoints (portal management for admins).
  // If the caller explicitly set Authorization, respect it.
  if (config.headers && config.headers.Authorization) return config;

  const url = config.url || "";
  const normalized = url.startsWith("/") ? url : `/${url}`;
  const isPortalAdminCall = normalized.startsWith("/portal/admin/");
  const isPortalClientCall = normalized.startsWith("/portal/") && !isPortalAdminCall;
  const token = isPortalClientCall
    ? getPortalToken()
    : localStorage.getItem("token");
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

api.interceptors.response.use(
  (response) => {
    // Batch #14.136 — Mode démo : toast clair quand le middleware retourne
    // {ok: true, demo_mode: true} sur un POST/PUT/PATCH/DELETE.
    try {
      const method = (response.config?.method || "").toUpperCase();
      const isMutation = method === "POST" || method === "PUT" || method === "PATCH" || method === "DELETE";
      if (isMutation && response.data?.demo_mode) {
        toast.success("Démo : ajout simulé (aucune donnée enregistrée).", {
          id: "demo-action-simulated",
        });
      }
    } catch { /* noop */ }
    return response;
  },
  async (error) => {
    // Batch #14.171 — Auto-refresh transparent sur 401 (admin uniquement).
    // Si /auth/refresh existe et que le refresh token est valide → on tente
    // de récupérer un nouveau access token et on rejoue la requête originale.
    // Les requêtes 401 concurrentes attendent la même promesse de refresh
    // pour éviter d'appeler /auth/refresh N fois en parallèle.
    const original = error.config;
    const status = error.response?.status;
    const reqUrl = original?.url || "";
    const isPortalCall = reqUrl.includes("/portal/") && !reqUrl.includes("/portal/admin/");
    const refreshToken = localStorage.getItem("adminRefreshToken");

    // Pas de retry si : pas 401, déjà retenté, call portail, ou pas de refresh token
    if (
      status !== 401
      || !refreshToken
      || isPortalCall
      || original?._retry
      || reqUrl.includes("/auth/refresh")
      || reqUrl.includes("/auth/login")
    ) {
      if (status === 401) {
        // Pas de refresh possible — on nettoie le token approprié
        if (isPortalCall) {
          if (isImpersonating()) clearImpersonation(); // session « en tant que » expirée (15 min)
          else { localStorage.removeItem("portalToken"); localStorage.removeItem("portalUser"); }
        } else {
          localStorage.removeItem("token");
          localStorage.removeItem("adminRefreshToken");
        }
      }
      return Promise.reject(error);
    }

    original._retry = true;

    // Si un refresh est déjà en cours, on attend la même promesse
    if (!api._refreshPromise) {
      api._refreshPromise = axios
        .post(`${API}/auth/refresh`, { refresh_token: refreshToken })
        .then((res) => {
          localStorage.setItem("token", res.data.access_token);
          localStorage.setItem("adminRefreshToken", res.data.refresh_token);
          return res.data.access_token;
        })
        .catch((refreshErr) => {
          // Refresh raté : on nettoie tout, le user devra re-login
          localStorage.removeItem("token");
          localStorage.removeItem("adminRefreshToken");
          throw refreshErr;
        })
        .finally(() => {
          // Reset après un court délai pour que d'autres 401 en file puissent réutiliser
          setTimeout(() => { api._refreshPromise = null; }, 0);
        });
    }

    try {
      const newAccessToken = await api._refreshPromise;
      original.headers.Authorization = `Bearer ${newAccessToken}`;
      return api(original);
    } catch (refreshErr) {
      // Si on est dans une page admin et que le refresh fail → redirige login
      if (typeof window !== "undefined"
          && window.location.pathname.startsWith("/admin")
          && !window.location.pathname.includes("/login")) {
        try { toast.error("Session expirée, veuillez vous reconnecter."); } catch { /* noop */ }
        setTimeout(() => { window.location.href = "/admin/login"; }, 800);
      }
      return Promise.reject(refreshErr);
    }
  }
);

/** Securely open a file in a new tab using Bearer auth (no token in URL). */
export async function secureOpen(path) {
  if (!path) {
    toast.error("Aucun fichier attache");
    return;
  }
  const token = localStorage.getItem("token") || getPortalToken() || "";
  let normalizedPath = path;
  if (normalizedPath.startsWith("/uploads/")) {
    normalizedPath = "/api" + normalizedPath;
  }
  const url = normalizedPath.startsWith("http") ? normalizedPath : `${BACKEND_URL}${normalizedPath}`;
  try {
    const res = await fetch(url, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) {
      const status = res.status;
      if (status === 404) toast.error("Fichier introuvable sur le serveur");
      else if (status === 401) toast.error("Session expiree, reconnectez-vous");
      else if (status === 403) toast.error("Acces refuse a ce fichier");
      else toast.error(`Erreur serveur (${status})`);
      return;
    }
    const blob = await res.blob();
    const blobUrl = URL.createObjectURL(blob);
    window.open(blobUrl, "_blank");
    setTimeout(() => URL.revokeObjectURL(blobUrl), 60000);
  } catch (err) {
    console.error("secureOpen error:", err);
    toast.error("Erreur reseau lors de l'ouverture du fichier");
  }
}

/** Securely download a file using Bearer auth (no token in URL). */
export async function secureDownloadFile(path, filename) {
  if (!path) {
    toast.error("Aucun fichier attache");
    return;
  }
  const token = localStorage.getItem("token") || getPortalToken() || "";
  // Normalize path: ensure it starts with /api/uploads/
  let normalizedPath = path;
  if (normalizedPath.startsWith("/uploads/")) {
    normalizedPath = "/api" + normalizedPath;
  }
  const url = normalizedPath.startsWith("http") ? normalizedPath : `${BACKEND_URL}${normalizedPath}`;
  try {
    const res = await fetch(url, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) {
      const status = res.status;
      if (status === 404) toast.error("Fichier introuvable sur le serveur");
      else if (status === 401) toast.error("Session expiree, reconnectez-vous");
      else if (status === 403) toast.error("Acces refuse a ce fichier");
      else toast.error(`Erreur serveur (${status})`);
      return;
    }
    const blob = await res.blob();
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = filename || "document";
    document.body.appendChild(a);
    a.click();
    URL.revokeObjectURL(a.href);
    a.remove();
  } catch (err) {
    console.error("secureDownload error:", err);
    toast.error("Erreur reseau lors du telechargement");
  }
}
